<script setup lang="ts">
import { computed, defineAsyncComponent, onUnmounted, ref, watch } from "vue";
import { PERSONAL, useBoard } from "../stores/board.js";
import { useViewport, type View } from "../stores/viewport.js";
import TaskTree from "./TaskTree.vue";
import GanttView from "./GanttView.vue";
import WebView from "./WebView.vue";
import CalendarView from "./CalendarView.vue";
import ViewMenu from "./ViewMenu.vue";
import PinnedViews from "./PinnedViews.vue";
import TaskDrawer from "./TaskDrawer.vue";
import TaskModal from "./TaskModal.vue";
import MessageBoard from "./MessageBoard.vue";
import FinanceView from "./FinanceView.vue";
import ShoppingView from "./ShoppingView.vue";
import RepoView from "./RepoView.vue";
import ProfileView from "./ProfileView.vue";
import WindowLayer from "./WindowLayer.vue";
import TeamSettingsView from "./TeamSettingsView.vue";
import IntegrationsModal from "./IntegrationsModal.vue";
import ClaudeStrip from "./ClaudeStrip.vue";
import TmuxLoading from "./TmuxLoading.vue";
import { useTaskRoute } from "../lib/taskRoute.js";
import { useAppShortcuts } from "../lib/appShortcuts.js";
import { useAutoPin } from "../lib/autoPin.js";
import { useFeedback } from "../lib/feedback.js";
import { toastMessage } from "../lib/toast.js";
import { deploying, reloadReady } from "../lib/deployReload.js";
import { pulse, pulseTarget } from "../lib/netPulse.js";

const reload = (): void => location.reload();

// Loaded on first open: only admins get the view. A placeholder shaped like it
// stands in at once, and a fetch that fails on a flaky connection is tried again.
const loadTmuxView = () => import("./TmuxView.vue");
const TmuxView = defineAsyncComponent({
  loader: loadTmuxView,
  loadingComponent: TmuxLoading,
  delay: 0,
  onError(err, retry, fail, attempts) {
    if (attempts <= 3) setTimeout(retry, attempts * 1_000);
    else fail();
  },
});
// The Pipeline: admins only, the same way.
const PipelineView = defineAsyncComponent({
  loader: () => import("./PipelineView.vue"),
  onError(err, retry, fail, attempts) {
    if (attempts <= 3) setTimeout(retry, attempts * 1_000);
    else fail();
  },
});
// The same for Automation: admins only, and its editor is not small.
const AutomationView = defineAsyncComponent({
  loader: () => import("./AutomationView.vue"),
  onError(err, retry, fail, attempts) {
    if (attempts <= 3) setTimeout(retry, attempts * 1_000);
    else fail();
  },
});

const board = useBoard();
const viewport = useViewport();

// Fetched ahead once the board is in and the browser is idle, for whoever may
// open the view, so switching to it later waits on nothing but its reads.
let prefetched = false;
watch(
  () => board.loaded && board.tmuxAvailable,
  (ready) => {
    if (!ready || prefetched) return;
    prefetched = true;
    const idle = window.requestIdleCallback ?? ((fn: () => void) => setTimeout(fn, 2_000));
    idle(() => void loadTmuxView().catch(() => undefined));
  },
  { immediate: true },
);
const treeWidth = ref(480);
useTaskRoute();
useAppShortcuts();
useAutoPin();
useFeedback();
const showIntegrations = ref(false);

const initials = (name: string): string =>
  name.split(/\s+/).map((p) => p[0] ?? "").join("").slice(0, 2) || name.slice(0, 2);

/** Who is online, and what they have open. */
const here = computed(() =>
  board.presence
    .map((p) => ({ ...p, user: board.users.get(p.userId) }))
    .filter((p) => p.user),
);

// The dot fades after data arrives and lights again with the next (lib/netPulse.ts).
// Only while connected: amber and red must stay plain to see.
const dotEl = ref<HTMLElement | null>(null);
watch([dotEl, () => board.status], ([el, status]) => {
  pulseTarget(status === "open" ? el : null);
  pulse();
});
onUnmounted(() => pulseTarget(null));

/** The dot opens the profile, and a second click goes back to where you were. */
let beforeProfile: View = "board";
function toggleProfile(): void {
  if (viewport.view === "profile") {
    viewport.view = beforeProfile;
  } else {
    beforeProfile = viewport.view;
    viewport.view = "profile";
  }
}

/** Drag the gutter between the two panes. */
function startResize(ev: PointerEvent): void {
  const startX = ev.clientX;
  const startW = treeWidth.value;
  const move = (e: PointerEvent): void => {
    treeWidth.value = Math.max(260, Math.min(900, startW + e.clientX - startX));
  };
  const up = (): void => {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", up);
  };
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", up);
}
</script>

<template>
  <div class="shell">
    <header class="topbar">
      <!-- No wordmark: the tab title says where you are, and the bar is better
           spent on the controls. The dot is the one thing that has to be here —
           it is the only sign the socket has dropped — and it doubles as the
           way into your own profile, which is where Sign out lives. -->
      <button
        class="dot-btn"
        :class="{ current: viewport.view === 'profile' }"
        type="button"
        :title="`${board.status === 'open' ? 'Connected' : board.status} — your profile and settings`"
        aria-label="Your profile and settings"
        @click="toggleProfile"
      >
        <span ref="dotEl" class="dot" :class="board.status"></span>
      </button>

      <!-- Which board is up: Personal, or a team's (with its thread and
           calendar). Hidden only for someone in no team, for whom Personal is
           all there is. A native select, so a phone gets its own picker
           rather than a menu squeezed into the bar. -->
      <select
        v-if="board.teams.length"
        class="team-select"
        :value="board.personalView ? PERSONAL : (board.teamId ?? '')"
        title="Personal, or a team — its board, messages and calendar"
        aria-label="Team"
        @change="board.switchTeam(($event.target as HTMLSelectElement).value)"
      >
        <option :value="PERSONAL">Personal</option>
        <option v-for="t in board.teams" :key="t.id" :value="t.id">{{ t.name }}</option>
      </select>

      <ViewMenu />
      <PinnedViews />

      <span class="spacer"></span>

      <!-- A deploy went out since this tab loaded, and has finished. Never reloaded
           for the reader: whatever they are halfway through is theirs to finish first. -->
      <button
        v-if="reloadReady"
        class="btn primary reload-btn"
        type="button"
        title="A new version is live — reload to get it"
        @click="reload"
      >
        <span class="wide-only">New version — reload</span><span class="narrow-only">Reload</span>
      </button>

      <button
        class="btn ghost icon-btn"
        type="button"
        title="Integrations: Claude Code, the Android app and notifications"
        aria-label="Integrations"
        @click="showIntegrations = true"
      >
        <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
          <rect x="1.5" y="2.5" width="13" height="11" rx="2" fill="none" stroke="currentColor" stroke-width="1.3" />
          <path d="M4.5 6.5 6.5 8l-2 1.5M8 10h3.5" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>

      <div class="avatars">
        <span
          v-for="p in here"
          :key="p.userId"
          class="avatar"
          :style="{ background: p.user!.color }"
          :title="p.user!.displayName + (p.taskId ? ' — viewing ' + (board.tasks.get(p.taskId)?.title ?? '') : '')"
        >{{ initials(p.user!.displayName) }}</span>
      </div>
    </header>

    <!-- A deploy of todos is running: the board is about to drop out for a
         moment, and the Reload above comes once it is done. No ×: it goes
         when the deploy does. -->
    <div v-if="deploying" class="deploy-banner" role="status">
      <span class="btn-spinner" aria-hidden="true"></span>
      <span><strong>Deploying a new version</strong> — the board may disconnect for a moment. Finish what you are typing; a Reload button appears once it is done.</span>
    </div>

    <ClaudeStrip />

    <CalendarView v-if="viewport.view === 'calendar'" />
    <MessageBoard v-else-if="viewport.view === 'messages'" />
    <FinanceView v-else-if="viewport.view === 'money'" />
    <ShoppingView v-else-if="viewport.view === 'shopping'" />
    <RepoView v-else-if="viewport.view === 'repos' && board.tmuxAvailable" />
    <AutomationView v-else-if="viewport.view === 'automation' && board.tmuxAvailable" />
    <TmuxView v-else-if="viewport.view === 'tmux' && board.tmuxAvailable" />
    <PipelineView v-else-if="viewport.view === 'pipeline' && board.tmuxAvailable" />
    <ProfileView v-else-if="viewport.view === 'profile'" />
    <TeamSettingsView v-else-if="viewport.view === 'team'" />

    <div v-else class="split" :class="'view-' + viewport.view">
      <section v-if="viewport.view === 'board'" class="pane-tree" :style="{ '--tree-w': treeWidth + 'px' }">
        <div class="pane-head">
          <span>Tasks</span>
          <span style="flex: 1"></span>
          <label class="head-check" title="Hide finished tasks (a done parent stays while anything under it is open)">
            <input type="checkbox" v-model="viewport.hideDone" />
            hide done
          </label>
          <label class="head-check" title="Only tasks assigned to you, and the parents they sit in">
            <input type="checkbox" v-model="viewport.onlyMine" />
            only mine
          </label>
          <select v-model="viewport.sortBy" class="head-select" title="Order the tree">
            <option value="manual">manual order</option>
            <option value="urgency">by urgency</option>
          </select>
        </div>
        <!-- Matches the gantt's hour axis so the two row lists start level. -->
        <div class="axis-gutter"></div>
        <TaskTree />
      </section>

      <div v-if="viewport.view === 'board'" class="gutter" @pointerdown.prevent="startResize"></div>

      <section v-if="viewport.view === 'web'" class="pane-web">
        <WebView />
      </section>
      <section v-else class="pane-gantt">
        <GanttView />
      </section>

      <TaskDrawer v-if="board.selected" />
    </div>

    <!-- The tmux view has no split to put the drawer in, so it opens over it. -->
    <TaskModal v-if="board.selected && viewport.view === 'tmux'" />

    <!-- Global settings, a window over any view. The tmux view and the Pipeline mount their own layers, which draw their windows too. -->
    <WindowLayer v-if="!((viewport.view === 'tmux' || viewport.view === 'pipeline') && board.tmuxAvailable)" :kinds="board.me?.isAdmin ? ['settings'] : []" />

    <IntegrationsModal v-if="showIntegrations" @close="showIntegrations = false" />

    <Transition name="toast">
      <div v-if="toastMessage" class="toast" role="status" aria-live="polite">{{ toastMessage }}</div>
    </Transition>
  </div>
</template>
